import { createStore } from 'vuex';
import sessionManager from '@/services/session';

export default createStore({
  state: {
    auth: {
      eLearning: { user: null, token: null },
      hmis: { user: null, token: null },
      rafiki360: { user: null, token: null },
      appeals: { user: null, token: null },
    },
    currentSystem: null, // Track the active system for UI purposes
    intendedRoute: null, // Store intended route for redirect after login
  },
  mutations: {
    setAuth(state, { user, token, system }) {
      if (!['eLearning', 'hmis', 'rafiki360', 'appeals'].includes(system)) {
        throw new Error(`Invalid system: ${system}`);
      }
      state.auth[system] = { user, token };
      state.currentSystem = system;
      localStorage.setItem(`${system}_user`, JSON.stringify(user));
      localStorage.setItem(`${system}_token`, token);
      localStorage.setItem(`${system}_session_start`, Date.now().toString());
      localStorage.setItem('currentSystem', system);
    },
    clearAuth(state, system = null) {
      if (system && ['eLearning', 'hmis', 'rafiki360', 'appeals'].includes(system)) {
        state.auth[system] = { user: null, token: null };
        localStorage.removeItem(`${system}_user`);
        localStorage.removeItem(`${system}_token`);
        localStorage.removeItem(`${system}_session_start`);
        if (state.currentSystem === system) {
          state.currentSystem = null;
          localStorage.removeItem('currentSystem');
        }
      } else {
        // Clear all systems
        state.auth.eLearning = { user: null, token: null };
        state.auth.hmis = { user: null, token: null };
        state.auth.rafiki360 = { user: null, token: null };
        state.auth.appeals = { user: null, token: null };
        state.currentSystem = null;
        localStorage.removeItem('eLearning_user');
        localStorage.removeItem('eLearning_token');
        localStorage.removeItem('eLearning_session_start');
        localStorage.removeItem('hmis_user');
        localStorage.removeItem('hmis_token');
        localStorage.removeItem('hmis_session_start');
        localStorage.removeItem('rafiki360_user');
        localStorage.removeItem('rafiki360_token');
        localStorage.removeItem('rafiki360_session_start');
        localStorage.removeItem('appeals_user');
        localStorage.removeItem('appeals_token');
        localStorage.removeItem('appeals_session_start');
        localStorage.removeItem('currentSystem');
      }
    },
    setIntendedRoute(state, route) {
      state.intendedRoute = route;
      localStorage.setItem('intendedRoute', route);
    },
    clearIntendedRoute(state) {
      state.intendedRoute = null;
      localStorage.removeItem('intendedRoute');
    },
  },
  actions: {
    login({ commit }, { data, token, system }) {
      if (!data || !token || !['eLearning', 'hmis', 'rafiki360', 'appeals'].includes(system)) {
        throw new Error('Invalid login data, token, or system');
      }
      commit('setAuth', { user: data, token, system });
      // Start session timer for the logged-in system
      sessionManager.login(system);
    },
    logout({ commit }, { system = null } = {}) {
      // Clear session timers
      sessionManager.logout(system);
      commit('clearAuth', system);
      // Let components handle navigation after logout
    },
    initializeAuth({ commit, state }) {
      const systems = ['eLearning', 'hmis', 'rafiki360', 'appeals'];
      let currentSystem = localStorage.getItem('currentSystem');
      let hasValidAuth = false;

      // Import setApiSystem here to avoid circular dependencies
      import('@/services/api').then(({ setApiSystem }) => {
        for (const system of systems) {
          const user = localStorage.getItem(`${system}_user`);
          const token = localStorage.getItem(`${system}_token`);
          if (user && token) {
            try {
              const parsedUser = JSON.parse(user);
              if (parsedUser && typeof parsedUser === 'object') {
                // Check if session is still valid
                if (sessionManager.isSessionExpired(system)) {
                  console.log(`Session expired for ${system}, clearing auth`);
                  localStorage.removeItem(`${system}_user`);
                  localStorage.removeItem(`${system}_token`);
                  localStorage.removeItem(`${system}_session_start`);
                  continue;
                }
                
                commit('setAuth', { user: parsedUser, token, system });
                hasValidAuth = true;
                if (!currentSystem) {
                  currentSystem = system;
                  localStorage.setItem('currentSystem', system);
                }
              } else {
                localStorage.removeItem(`${system}_user`);
                localStorage.removeItem(`${system}_token`);
                localStorage.removeItem(`${system}_session_start`);
              }
            } catch (e) {
              console.error(`Failed to parse ${system}_user from localStorage:`, e);
              localStorage.removeItem(`${system}_user`);
              localStorage.removeItem(`${system}_token`);
              localStorage.removeItem(`${system}_session_start`);
            }
          }
        }

        if (!hasValidAuth) {
          localStorage.removeItem('currentSystem');
          commit('clearAuth');
        }

        // Restore intended route
        const intendedRoute = localStorage.getItem('intendedRoute');
        if (intendedRoute) {
          commit('setIntendedRoute', intendedRoute);
        }

        // Set API system based on current authentication state
        const activeSystem = state.currentSystem || localStorage.getItem('currentSystem') || 'eLearning';
        setApiSystem(activeSystem);
        console.log(`API system set to: ${activeSystem}`);
      }).catch(error => {
        console.error('Failed to import setApiSystem:', error);
        // Fallback to eLearning system
        import('@/services/api').then(({ setApiSystem }) => {
          setApiSystem('eLearning');
          console.log('API system set to eLearning (fallback)');
        });
      });
    },
  },
  getters: {
    isAuthenticated: (state) => (system) => !!state.auth[system]?.token,
    currentUser: (state) => (system) => state.auth[system]?.user || null,
    token: (state) => (system) => state.auth[system]?.token || null,
    currentSystem: (state) => state.currentSystem,
    intendedRoute: (state) => state.intendedRoute,
  },
});